import { gql } from '@apollo/client';

import {
  LINK_ADDRESSES_FIELDS,
  SMS_CAMPAIGN_FIELDS,
  SMS_MESSAGE_FIELDS,
  SMS_PREVIEW_FIELDS,
  SMS_PROVIDER_STATUS_FIELDS,
  SMS_REMINDER_PLAN_FIELDS,
  SMS_SETTINGS_FIELDS,
  STUDENT_SMS_STATE_FIELDS,
  TEACHER_SMS_STATE_FIELDS,
} from '../fragments/sms';

/**
 * ТЗ 0081 — SMS студентам. Имена операций намеренно отличаются от полей
 * бэкенда (`smsJournal` → `SmsJournalPage` и т. д.): иначе codegen склеил бы
 * тип результата с типом поля.
 */

/** Журнал всех SMS юрлица: фильтры, страница, итоги по найденному. */
export const SmsJournalPageQuery = gql`
  ${SMS_MESSAGE_FIELDS}
  query SmsJournalPage(
    $topic: String
    $status: String
    $dlrStatus: String
    $studentId: ID
    $groupId: ID
    $teacherId: ID
    $contractId: ID
    $campaignId: ID
    $createdById: ID
    $createdFrom: Date
    $createdTo: Date
    $phoneTail: String
    $search: String
    $leadId: ID
    $candidateId: ID
    $offset: Int
    $limit: Int
  ) {
    smsJournal(
      topic: $topic
      status: $status
      dlrStatus: $dlrStatus
      studentId: $studentId
      groupId: $groupId
      teacherId: $teacherId
      contractId: $contractId
      campaignId: $campaignId
      createdById: $createdById
      createdFrom: $createdFrom
      createdTo: $createdTo
      phoneTail: $phoneTail
      search: $search
      leadId: $leadId
      candidateId: $candidateId
      offset: $offset
      limit: $limit
    ) {
      total
      totalCost
      totalSegments
      items {
        ...SmsMessageFields
      }
    }
  }
`;

/** Что встречается в журнале: темы, авторы, рассылки — для выпадашек. */
export const SmsJournalFilterOptionsQuery = gql`
  query SmsJournalFilterOptions {
    smsJournalFacets {
      topics
      authors {
        id
        name
      }
      campaigns {
        id
        title
      }
    }
  }
`;

/** SMS в карточке студента: можно ли, телефон, фактуры для ссылки, история. */
export const StudentSmsStateQuery = gql`
  ${STUDENT_SMS_STATE_FIELDS}
  query StudentSmsState($studentId: ID!) {
    studentSms(studentId: $studentId) {
      ...StudentSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS студенту. */
export const StudentSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query StudentSmsPreview(
    $studentId: ID!
    $text: String!
    $link: SmsLinkInput
  ) {
    previewStudentSms(studentId: $studentId, text: $text, link: $link) {
      ...SmsPreviewFields
    }
  }
`;

/** SMS в карточке преподавателя: можно ли, телефон, документы, история. */
export const TeacherSmsStateQuery = gql`
  ${TEACHER_SMS_STATE_FIELDS}
  query TeacherSmsState($teacherId: ID!) {
    teacherSms(teacherId: $teacherId) {
      ...TeacherSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS преподавателю. */
export const TeacherSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query TeacherSmsPreview(
    $teacherId: ID!
    $text: String!
    $link: TeacherSmsLinkInput
  ) {
    previewTeacherSms(teacherId: $teacherId, text: $text, link: $link) {
      ...SmsPreviewFields
    }
  }
`;

/** Кому уйдёт рассылка по группе и почему кому-то нет. */
export const GroupSmsCandidatesQuery = gql`
  query GroupSmsCandidates($groupId: ID!, $mode: String) {
    smsCampaignCandidates(groupId: $groupId, mode: $mode) {
      studentId
      studentName
      phoneMasked
      reason
      invoiceId
      invoiceNumber
      amount
      currency
      dueDate
    }
  }
`;

/**
 * Предпросмотр рассылки на первом получателе и счёт получателей.
 * `studentIds` — выбор сотрудника (не задан — все кандидаты режима).
 */
export const GroupSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query GroupSmsPreview(
    $groupId: ID!
    $text: String!
    $link: SmsLinkInput
    $mode: String
    $studentIds: [ID!]
  ) {
    previewSmsCampaign(
      groupId: $groupId
      text: $text
      link: $link
      mode: $mode
      studentIds: $studentIds
    ) {
      sample {
        ...SmsPreviewFields
      }
      recipients
      excluded
      total
      reason
      message
    }
  }
`;

/** История рассылок группы (или всех, без `groupId`). */
export const GroupSmsCampaignsQuery = gql`
  ${SMS_CAMPAIGN_FIELDS}
  query GroupSmsCampaigns($groupId: ID) {
    smsCampaigns(groupId: $groupId) {
      ...SmsCampaignFields
    }
  }
`;

/** Одна рассылка — опрос итогов, пока она не закончилась. */
export const SmsCampaignProgressQuery = gql`
  ${SMS_CAMPAIGN_FIELDS}
  query SmsCampaignProgress($id: ID!) {
    smsCampaign(id: $id) {
      ...SmsCampaignFields
    }
  }
`;

export const SmsSettingsViewQuery = gql`
  ${SMS_SETTINGS_FIELDS}
  query SmsSettingsView {
    smsSettings {
      ...SmsSettingsFields
    }
  }
`;

/** ТЗ 0084: адреса ссылок (анкета, LMS, сократитель, Documenso). */
export const LinkAddressesViewQuery = gql`
  ${LINK_ADDRESSES_FIELDS}
  query LinkAddressesView {
    linkAddresses {
      ...LinkAddressesFields
    }
  }
`;

/** Баланс и имя отправителя; `refresh: true` — спросить SMSAPI сейчас. */
export const SmsProviderStateQuery = gql`
  ${SMS_PROVIDER_STATUS_FIELDS}
  query SmsProviderState($refresh: Boolean) {
    smsProviderStatus(refresh: $refresh) {
      ...SmsProviderStatusFields
    }
  }
`;

export const SmsReminderPlansQuery = gql`
  ${SMS_REMINDER_PLAN_FIELDS}
  query SmsReminderPlans {
    reminderPlans {
      ...SmsReminderPlanFields
    }
  }
`;

/** Сухой прогон лесенки: что робот отправил бы сейчас, без отправки. */
export const SmsReminderDryRunQuery = gql`
  query SmsReminderDryRun($topic: String!, $limit: Int) {
    previewSmsReminders(topic: $topic, limit: $limit) {
      topic
      subjectId
      subjectLabel
      step
      dueAt
      dueNow
      phoneMasked
      reason
      state
      condition
      body
      segments
    }
  }
`;
